import React, { useEffect, useState } from "react";
import { Loader2, Save, PenLine, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner, Pill } from "./Primitives";

export default function AdminSubmissions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/admin/coursework/submissions").then((r) => {
      setSubs(r.data);
      setDrafts(Object.fromEntries(r.data.map((s) => [s.id, s.feedback || ""])));
      setScores(Object.fromEntries(r.data.map((s) => [s.id, s.score ?? ""])));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async (id) => {
    const raw = scores[id];
    if (raw !== "" && raw != null && (Number(raw) < 0 || Number(raw) > 100)) return toast.error("Score must be 0–100");
    setSaving(id);
    try {
      await api.post(`/admin/submissions/${id}/feedback`, { feedback: drafts[id] || "", score: raw === "" ? null : Number(raw) });
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, feedback: drafts[id], score: raw === "" ? null : Number(raw) } : s)));
      toast.success("Grade saved — the student will see it on their assignment page");
    } catch {
      toast.error("Could not save feedback");
    } finally {
      setSaving("");
    }
  };

  if (loading) return <Spinner />;
  if (subs.length === 0) {
    return (
      <p className="mt-10 border border-dashed border-edge p-12 text-center text-muted" data-testid="submissions-empty">
        No student submissions yet. They'll appear here once students complete published assignments.
      </p>
    );
  }

  const groups = [];
  const bySlug = {};
  for (const s of subs) {
    if (!bySlug[s.coursework_slug]) {
      bySlug[s.coursework_slug] = { slug: s.coursework_slug, title: s.coursework_title, items: [] };
      groups.push(bySlug[s.coursework_slug]);
    }
    bySlug[s.coursework_slug].items.push(s);
  }

  return (
    <div className="mt-10 space-y-12" data-testid="admin-submissions">
      {groups.map((g) => (
        <div key={g.slug}>
          <div className="mb-4 flex items-center gap-3">
            <h2 className="font-serif text-2xl text-bone">{g.title}</h2>
            <Pill tone="muted">{g.items.length} {g.items.length === 1 ? "submission" : "submissions"}</Pill>
          </div>
          <div className="space-y-6">
            {g.items.map((s) => (
              <div key={s.id} className="border border-edge bg-ash p-6" data-testid={`submission-card-${s.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <p className="text-bone">{s.author_name}</p>
                    {s.score != null && (
                      <span className="border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold" data-testid={`score-badge-${s.id}`}>
                        {s.score}%
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                  </p>
                </div>

                <div className="mt-4 space-y-4">
                  {(s.questions || []).map((q, i) => (
                    <div key={i} className="border-l-2 border-edge pl-4">
                      <p className="text-sm text-bone"><span className="mr-2 font-mono text-gold">{i + 1}.</span>{q}</p>
                      <p className="mt-1 text-sm text-[#cfc9bd]">{(s.answers?.[i] || "").trim() || <span className="text-muted">— no answer —</span>}</p>
                    </div>
                  ))}
                  {s.writing && (
                    <div className="border-l-2 border-gold/50 pl-4">
                      <p className="text-sm text-bone"><PenLine className="mr-2 inline h-3.5 w-3.5 text-gold" />{s.writing_prompt || "Writing task"}</p>
                      <p className="mt-1 whitespace-pre-line text-sm text-[#cfc9bd]">{s.writing}</p>
                    </div>
                  )}
                </div>

                <div className="mt-5 border-t border-edge pt-4">
                  <label className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
                    <MessageSquareQuote className="h-4 w-4" /> Grade & written feedback
                  </label>
                  <div className="mb-2 flex items-center gap-2">
                    <input type="number" min={0} max={100} value={scores[s.id] ?? ""} placeholder="—"
                      onChange={(e) => setScores((sc) => ({ ...sc, [s.id]: e.target.value }))}
                      data-testid={`score-input-${s.id}`}
                      className="w-24 border border-edge bg-ink px-3 py-2 text-sm text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
                    <span className="font-mono text-sm text-muted">% score (0–100, optional)</span>
                  </div>
                  <textarea value={drafts[s.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                    rows={3} placeholder="Encouragement, corrections, next steps…" data-testid={`feedback-input-${s.id}`}
                    className="w-full border border-edge bg-ink px-4 py-3 text-sm text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
                  <button onClick={() => save(s.id)} disabled={saving === s.id} data-testid={`save-feedback-${s.id}`}
                    className="mt-2 flex items-center gap-2 border border-gold bg-gold/10 px-4 py-2 text-xs font-medium text-gold hover:bg-gold hover:text-ink disabled:opacity-50">
                    {saving === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {s.feedback || s.score != null ? "Update grade" : "Send grade"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
